import { useRef } from "react";
import { UseMutationOptions } from "react-query";
import { EnumCommentEntityType } from "src/api/client/comment-v2.api.types";
import { API_COMMENT_REPLY, API_COMMENT_SEARCH_V2_URL, API_COMMENT_V2_URL } from "src/api/constants/comment-v2-urls";
import { TComment } from "src/components/common/comments-area/comment/comment.types";
import { resolveCommentsLength } from "src/components/common/comments-area/utils/resolve-comments-length";
import { MAX_ATTEMPTS, RETRY_DELAY_MS } from "src/consts/retry";
import { IInfiniteQueryOptions, useInfiniteQuery } from "src/hooks/react-query/use-infinite-query";
import { useMutation } from "src/hooks/react-query/use-mutation";

export interface ICommentsResponse {
    comments: ReadonlyArray<TComment>;
    commentsCount: number;
}
const DEFAULT_NUMBER_OF_REPLIES = 2;
const DEFAULT_SKIP = 0;
const DEFAULT_TAKE = 3;

const SHOW_MORE_NUMBER_OF_REPLIES = 5;
const SHOW_MORE_NUMBER_OF_COMMENTS = 5;

const getNextPageParam = (lastPage: ICommentsResponse, pages: ICommentsResponse[]) => {
    const pagesCommentsCount = resolveCommentsLength(pages.flatMap(item => item.comments));
    const commentsLeft = lastPage.commentsCount - pagesCommentsCount;

    return commentsLeft ? pagesCommentsCount : undefined;
};

export const useCommentsForEntityInfiniteQuery = (entityId: string) => {
    const takeRef = useRef(DEFAULT_TAKE);
    const skipRef = useRef<undefined | number>(DEFAULT_SKIP);

    return useInfiniteQuery<ICommentsResponse>(
        API_COMMENT_SEARCH_V2_URL,
        {
            getNextPageParam: (...params) => {
                takeRef.current = SHOW_MORE_NUMBER_OF_COMMENTS;
                skipRef.current = getNextPageParam(...params);
                return getNextPageParam(...params);
            },
            refetchOnWindowFocus: false,
            method: "POST",
            body: JSON.stringify({
                criteria: [
                    [
                        {
                            entityId,
                            entityType: EnumCommentEntityType.ExternalNewsArticle,
                        },
                    ],
                ],
                skip: skipRef.current,
                take: takeRef.current,
                numberOfReplies: DEFAULT_NUMBER_OF_REPLIES,
                onlyActive: false,
                includeScoring: true,
                sortDescending: false,
                flattenReplies: false,
            }),
            retry: MAX_ATTEMPTS,
            retryDelay: RETRY_DELAY_MS,
        },
        undefined,
        [API_COMMENT_SEARCH_V2_URL, entityId]
    );
};

export const useRepliesForCommentInfiniteQuery = (commentId: string, options: IInfiniteQueryOptions<ICommentsResponse, unknown>) => {
    const queryParams = new URLSearchParams({
        commentId,
        take: `${SHOW_MORE_NUMBER_OF_REPLIES}`,
        onlyActive: "false",
    });
    return useInfiniteQuery<ICommentsResponse>(
        API_COMMENT_REPLY,
        {
            getNextPageParam,
            refetchOnWindowFocus: false,
            queryParams,
            ...options,
        },
        { defaultPageParamNumber: SHOW_MORE_NUMBER_OF_REPLIES, pageParamName: "skip" },
        [API_COMMENT_REPLY, commentId]
    );
};

type TEntityReference = {
    entityId?: string;
    entityType: EnumCommentEntityType;
    status?: number;
    type?: number;
};

interface IAddCommentVariables {
    entityReferences: TEntityReference[];
    parentCommentId?: string;
    message: string;
}
interface IUpdateCommentVariables {
    commentId: string;
    message: string;
}

export const useAddCommentMutation = (options: UseMutationOptions<TComment, unknown, IAddCommentVariables>) =>
    useMutation<TComment, unknown, IAddCommentVariables>(API_COMMENT_V2_URL, options);

export const useUpdateCommentMutation = (options: UseMutationOptions<TComment, unknown, IUpdateCommentVariables>) => {
    return useMutation<TComment, unknown, IUpdateCommentVariables>(API_COMMENT_V2_URL, {
        method: "PUT",
        ...options,
    });
};

type TDeleteCommentMutation = {
    commentId: string;
    options: UseMutationOptions;
};

export function useDeleteCommentMutation({ commentId, options }: TDeleteCommentMutation) {
    const { mutatePromise } = useMutation(`${API_COMMENT_V2_URL}/${commentId}`, { method: "DELETE", ...options });

    return mutatePromise;
}
